'use client';

import { DeviceContext, DeviceData } from 'common-nextjs';
import { Session } from 'fetcher-session';
import { AppProgressBar as ProgressBar } from 'next-nprogress-bar';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';
import ModalsGroup from '~/components/Modal/ModalsGroup';
import ModalsContextProvider from '~/contexts/ModalsContext';
import SaleContainer from '~/contexts/SaleContext';
import SessionContext from '~/contexts/SessionContext';
import { useCustomEvent } from '~/lib/events/customEvents';
import { PrivacyConsentProvider } from '~/lib/privacy/context.client';
import { PrivacyConsentModel } from '~/lib/privacy/model';
import analyticsInitialize from '~/services/analytics/analyticsInitialize';
import analyticsPageView from '~/services/analytics/analyticsPageView';
import {
  trackingIdentify,
  trackingIdentifyAnonymous,
} from '~/services/analytics/identify';
import { IAMUser } from '~/typings/services/iam/auth';

if (typeof window !== 'undefined') {
  analyticsInitialize();
}

export function LegacyClientSideProviders({
  children,
  initialDeviceState,
  initialPrivacyModel,
  initialSessionState,
  initialUser,
}: {
  children: React.ReactNode;
  initialDeviceState?: DeviceData;
  initialPrivacyModel: PrivacyConsentModel;
  initialSessionState?: Session;
  initialUser?: IAMUser;
}) {
  return (
    <DeviceContext value={initialDeviceState}>
      <SessionContext
        initialState={
          initialSessionState
            ? { session: initialSessionState, iamUser: initialUser }
            : undefined
        }
      >
        <SaleContainer>{children}</SaleContainer>
      </SessionContext>
    </DeviceContext>
  );
}

export function ClientSideProviders({
  children,
  initialPrivacyModel,
}: {
  children: React.ReactNode;
  initialPrivacyModel: PrivacyConsentModel;
}) {
  return (
    <PrivacyConsentProvider initialModel={initialPrivacyModel}>
      {children}
    </PrivacyConsentProvider>
  );
}

export function LegacyModals({ children }: { children: React.ReactNode }) {
  return (
    <ModalsContextProvider>
      {children}

      <ModalsGroup />
    </ModalsContextProvider>
  );
}

export function ClientSideGlobals() {
  return (
    <>
      <ProgressBar
        height="4px"
        color="#02c874"
        options={{ showSpinner: false }}
        shallowRouting={false}
      />

      <GlobalEvents />
    </>
  );
}

export function GlobalAnalytics({
  username,
  externalId,
  email,
  phoneNumber,
}: {
  email?: string;
  username?: string;
  externalId?: string;
  phoneNumber?: string;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (username && externalId) {
      trackingIdentify({
        email,
        username,
        external_id: externalId,
        phoneNumber,
      });
    } else {
      trackingIdentifyAnonymous();
    }
  }, []);

  const stringifiedParams = searchParams?.toString();
  useEffect(() => {
    const url = `${pathname}${
      stringifiedParams ? `?${stringifiedParams}` : ''
    }`;
    analyticsPageView(url);
  }, [pathname, stringifiedParams]);

  return null;
}

function GlobalEvents() {
  const router = useRouter();

  // Refresh app directory if requested from legacy architecture.
  // Mainly used for things like auth or item actions on app dir pages.
  useCustomEvent('sls_app_dir_refresh_from_legacy', () => {
    router.refresh();
  });

  return null;
}
